if (!customElements.get("stl-look-slider")) {
  class StlLookSlider extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.lastWindowWidth = 0;
      this.onSectionLoad = this.init.bind(this);

      if (typeof Shopify !== "undefined" && Shopify.designMode) {
        window.addEventListener("shopify:section:load", this.onSectionLoad);
      }
    }

    connectedCallback() {
      this.scheduleInit();
    }

    disconnectedCallback() {
      this.teardown();
    }

    scheduleInit(attempt = 0) {
      if (typeof Swiper === "undefined") {
        if (attempt > 40) return;
        window.setTimeout(() => this.scheduleInit(attempt + 1), 50);
        return;
      }
      this.init();
    }

    teardown() {
      this.abortController?.abort();

      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    init() {
      if (typeof Swiper === "undefined") return;

      this.teardown();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.lastWindowWidth = window.innerWidth;

      const swiperEl = this;
      const sliderRoot =
        this.closest(".stl-carousel__slider") || this.parentElement;
      const swiperOptions =
        JSON.parse(this.getAttribute("data-swiper-options") || "{}") || {};

      const isMobile = window.matchMedia("(max-width: 749px)").matches;
      const isDesktopWide = window.matchMedia("(min-width: 990px)").matches;
      const slideCount = swiperOptions.slideCount || 0;
      // Loop + centeredSlides + slidesPerView:auto is unstable on mobile (jumping).
      // Use rewind instead so swipe stays smooth end-to-end.
      const useRewind = slideCount >= 2 && !isDesktopWide;

      this.prepareVideos();

      const sliderOptions = {
        slidesPerView: "auto",
        slidesPerGroup: 1,
        centeredSlides: false,
        loop: false,
        rewind: useRewind,
        speed: isMobile ? 260 : 400,
        threshold: isMobile ? 4 : 8,
        longSwipesRatio: isMobile ? 0.2 : 0.25,
        touchRatio: isMobile ? 1.15 : 1,
        followFinger: true,
        simulateTouch: true,
        grabCursor: true,
        allowTouchMove: true,
        watchOverflow: false,
        observer: false,
        observeParents: false,
        preventClicks: true,
        preventClicksPropagation: true,
        touchStartPreventDefault: false,
        spaceBetween: swiperOptions.spaceBetweenMobile || 0,
        initialSlide: 0,
        breakpoints: {
          750: {
            slidesPerView: "auto",
            centeredSlides: false,
            loop: false,
            rewind: useRewind,
            allowTouchMove: true,
            spaceBetween:
              swiperOptions.spaceBetweenTablet ||
              swiperOptions.spaceBetweenDesktop ||
              0
          },
          990: {
            slidesPerView: "auto",
            centeredSlides: false,
            loop: false,
            rewind: false,
            allowTouchMove: true,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 0
          }
        },
        on: {
          init: swiper => {
            this.prepareVideos();
            this.bindInteractions(signal);
            this.bindNavigation(sliderRoot, swiperOptions, signal);
            if (swiper.activeIndex !== 0) {
              swiper.slideTo(0, 0);
            }
            swiper.update();
            requestAnimationFrame(() => {
              if (swiper.activeIndex !== 0) {
                swiper.slideTo(0, 0);
              }
              this.playActiveSlide(swiper);
            });
          },
          slideChange: swiper => {
            this.playActiveSlide(swiper);
          },
          slideChangeTransitionEnd: swiper => {
            this.playActiveSlide(swiper);
          },
          resize: swiper => {
            const width = window.innerWidth;
            if (width === this.lastWindowWidth) return;
            this.lastWindowWidth = width;
            swiper.update();
          }
        }
      };

      this.swiper = new Swiper(swiperEl, sliderOptions);

      if (!this.breakpointListener) {
        this.breakpointListener = () => this.init();
        window
          .matchMedia("(min-width: 990px)")
          .addEventListener("change", this.breakpointListener);
        window
          .matchMedia("(max-width: 749px)")
          .addEventListener("change", this.breakpointListener);
      }
    }

    prepareVideos() {
      this.querySelectorAll("video").forEach(video => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.setAttribute("muted", "");
      });
    }

    bindNavigation(sliderRoot, swiperOptions, signal) {
      if (!swiperOptions.navigation || !sliderRoot) return;

      const prevEl = sliderRoot.querySelector(swiperOptions.navigation.prevEl);
      const nextEl = sliderRoot.querySelector(swiperOptions.navigation.nextEl);
      if (!prevEl || !nextEl) return;

      const slideCount = swiperOptions.slideCount || 0;
      if (slideCount < 2) return;

      prevEl.addEventListener(
        "click",
        event => {
          event.preventDefault();
          this.swiper?.slidePrev();
        },
        { signal }
      );

      nextEl.addEventListener(
        "click",
        event => {
          event.preventDefault();
          this.swiper?.slideNext();
        },
        { signal }
      );
    }

    bindInteractions(signal) {
      const handleMediaClick = event => {
        if (!this.swiper || this.swiper.allowClick === false) return;
        if (event.target.closest(".stl-product-bar")) return;

        const media = event.target.closest(".stl-carousel__media");
        if (!media || !this.contains(media)) return;

        const slide = media.closest(".swiper-slide");
        if (!slide) return;

        const isDesktopWide = window.matchMedia("(min-width: 990px)").matches;
        const isActive = slide.classList.contains("swiper-slide-active");

        if (!isDesktopWide && !isActive) {
          const index = this.getSlideIndex(slide);
          if (index !== null) {
            if (this.swiper.params.loop) {
              this.swiper.slideToLoop(index);
            } else {
              this.swiper.slideTo(index);
            }
            return;
          }
        }

        this.playOneSlide(slide);
      };

      this.addEventListener("click", handleMediaClick, { signal });
    }

    getSlideIndex(slide) {
      const loopIndex = slide.getAttribute("data-swiper-slide-index");
      if (loopIndex !== null && loopIndex !== "") {
        return parseInt(loopIndex, 10);
      }
      return Array.from(slide.parentElement?.children || []).indexOf(slide);
    }

    getSlideVideo(slide) {
      if (!slide) return null;

      const visibleInner = Array.from(
        slide.querySelectorAll(".stl-carousel__media-inner")
      ).find(inner => inner.offsetParent !== null);

      return (
        visibleInner?.querySelector("video") ||
        slide.querySelector("video") ||
        null
      );
    }

    pauseSlideMedia(slide) {
      if (!slide) return;

      slide.querySelectorAll("video").forEach(video => video.pause());

      slide.querySelectorAll(".js-youtube").forEach(iframe => {
        iframe.contentWindow?.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*"
        );
      });

      slide.querySelectorAll(".js-vimeo").forEach(iframe => {
        iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
      });
    }

    playSlideMedia(slide) {
      if (!slide) return;

      const video = this.getSlideVideo(slide);
      if (video) {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        const play = () => {
          const playPromise = video.play();
          if (playPromise !== undefined) playPromise.catch(() => {});
        };
        if (video.readyState >= 2) {
          play();
        } else {
          video.addEventListener("canplay", play, { once: true });
          play();
        }
        return;
      }

      const mediaInner =
        slide.querySelector(".stl-carousel__media-inner") || slide;

      const youtube = mediaInner.querySelector(".js-youtube");
      if (youtube) {
        youtube.contentWindow?.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*"
        );
        return;
      }

      const vimeo = mediaInner.querySelector(".js-vimeo");
      if (vimeo) {
        vimeo.contentWindow?.postMessage('{"method":"play"}', "*");
      }
    }

    pauseAllExcept(activeSlide, swiper) {
      const instance = swiper || this.swiper;
      if (!instance?.slides) return;

      instance.slides.forEach(slide => {
        if (slide !== activeSlide) this.pauseSlideMedia(slide);
      });
    }

    setPlayingState(slide) {
      this.querySelectorAll(".stl-carousel__media.is-playing").forEach(media => {
        media.classList.remove("is-playing");
      });
      slide?.querySelector(".stl-carousel__media")?.classList.add("is-playing");
    }

    playOneSlide(slide, swiper) {
      if (!slide) return;
      this.pauseAllExcept(slide, swiper);
      this.playSlideMedia(slide);
      this.setPlayingState(slide);
    }

    playActiveSlide(swiper) {
      const instance = swiper || this.swiper;
      const activeSlide = instance?.slides?.[instance.activeIndex];
      if (!activeSlide) return;
      this.playOneSlide(activeSlide, instance);
    }
  }

  customElements.define("stl-look-slider", StlLookSlider);
}
